import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, DotsSixVertical } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = [
  { value: 'gravida', label: 'Super Barriguinhas' },
  { value: 'pos_parto', label: 'Super Mamãs' },
  { value: 'pre_concecao', label: 'Super Pré Mamãs' },
  { value: 'introducao_alimentar', label: 'Super Bebés' },
  { value: 'geral', label: 'Geral' },
];

export default function GuidesEditorPage() {
  useRequireAdmin();
  const [selected, setSelected] = useState(null);
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [guides, setGuides] = useState([]);
  // Drag
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const fetchGuides = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/guides`); setGuides(data); } catch {}
  }, []);
  useEffect(() => { fetchGuides(); }, [fetchGuides]);

  const loadGuide = async (prog) => {
    setSelected(prog);
    try { const { data } = await axios.get(`${API}/api/guides/${prog}`); setSections(data?.sections || []); } catch { setSections([]); }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try { await axios.put(`${API}/api/guides/${selected}`, { sections }); toast.success('Guia guardado'); fetchGuides(); } catch { toast.error('Erro'); }
    finally { setSaving(false); }
  };

  const addSection = () => {
    setSections([...sections, { id: Date.now().toString(), title: 'Nova secção', content: '', order: sections.length }]);
  };
  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));
  const updateSection = (idx, field, value) => { const s = [...sections]; s[idx] = { ...s[idx], [field]: value }; setSections(s); };

  // Drag handlers
  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = (e, targetIdx) => {
    e.preventDefault(); setOverIdx(null);
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const arr = [...sections];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx > dragIdx ? targetIdx - 1 : targetIdx, 0, moved);
    setSections(arr); setDragIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div className="fade-in" data-testid="guides-editor-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Guias de Utilização</h2>
          <p className="text-xs text-brand-muted mt-0.5">Conteúdo de ajuda que as clientes veem na app</p>
        </div>
      </div>

      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 flex-wrap">
        {PROGRAMS.map(p => {
          const has = guides.some(g => g.program_id === p.value);
          return (
            <button key={p.value} onClick={() => loadGuide(p.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${selected === p.value ? 'bg-brand-green text-white' : has ? 'bg-brand-green/10 text-brand-green border border-brand-green/20' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
              data-testid={`guide-prog-${p.value}`}>
              {p.label} {has && selected !== p.value && '*'}
            </button>
          );
        })}
      </div>

      {!selected ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <p className="text-brand-muted text-sm">Seleciona um programa para editar o guia de utilização.</p>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-medium text-sm text-brand-text">{PROGRAMS.find(p => p.value === selected)?.label}</h3>
            <div className="flex gap-2">
              <button onClick={addSection} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:bg-brand-bg" data-testid="add-guide-section">
                <Plus size={12} className="inline mr-1" /> Secção
              </button>
              <button onClick={save} disabled={saving} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50" data-testid="save-guide-btn">
                <FloppyDisk size={12} /> {saving ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>

          {sections.length === 0 ? (
            <p className="text-center text-brand-muted text-sm py-8 bg-white border border-brand-border rounded-2xl">Sem secções. Adiciona a primeira.</p>
          ) : (
            <div className="space-y-3">
              {sections.map((sec, idx) => (
                <div key={sec.id || idx}
                  draggable onDragStart={e => handleDragStart(e, idx)} onDragEnd={handleDragEnd}
                  onDragOver={e => handleDragOver(e, idx)} onDrop={e => handleDrop(e, idx)}
                  className={`bg-white border rounded-2xl p-4 transition-all ${dragIdx === idx ? 'opacity-40' : ''} ${overIdx === idx && dragIdx !== idx ? 'ring-2 ring-brand-green/40 bg-brand-green/5' : 'border-brand-border'}`}
                  data-testid={`guide-section-${idx}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green"><DotsSixVertical size={14} /></div>
                    <InlineEdit value={sec.title} onSave={v => updateSection(idx, 'title', v)} className="font-heading font-medium text-sm text-brand-text flex-1" testId={`guide-sec-title-${idx}`} />
                    <button onClick={() => removeSection(idx)} className="text-brand-placeholder hover:text-red-500 p-1"><Trash size={13} /></button>
                  </div>
                  <Textarea
                    value={sec.content || ''} onChange={e => updateSection(idx, 'content', e.target.value)}
                    className="rounded-xl border-brand-border text-sm" rows={3}
                    placeholder="Conteúdo da secção... (suporta HTML simples: <b>, <i>, <ul>, <li>)"
                    data-testid={`guide-sec-content-${idx}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
